'use client';
import { useState } from 'react';
import { cn } from '../cn';
import type { SavedView } from './document-list-shell-types';

interface TabsProps {
  views: SavedView[];
  activeView: string;
  onViewChange: (id: string) => void;
  onSavedViewAdd?: (label: string) => void;
  onSavedViewDelete?: (id: string) => void;
}

/**
 * Saved views tabs: butoane orizontale + buton "+ Salvează șablon" opțional
 * (cu prompt simplu pentru label) + buton × pentru ștergerea view-urilor custom.
 */
export function DocumentListShellTabs({
  views,
  activeView,
  onViewChange,
  onSavedViewAdd,
  onSavedViewDelete,
}: TabsProps) {
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  const submitNew = () => {
    if (newLabel.trim() && onSavedViewAdd) {
      onSavedViewAdd(newLabel.trim());
      setNewLabel('');
      setAdding(false);
    }
  };

  return (
    <nav
      aria-label="Vizualizări salvate"
      className="flex flex-wrap items-center gap-1 border-b border-neutral-200 px-2 pt-1"
    >
      {views.map((v) => {
        const active = v.id === activeView;
        return (
          <span key={v.id} className="relative inline-flex">
            <button
              type="button"
              onClick={() => onViewChange(v.id)}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium transition-colors',
                active
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900',
              )}
            >
              {v.label}
              {v.count !== undefined && v.count > 0 && (
                <span className={cn(
                  'tabular-nums rounded-full px-1.5 py-0.5 text-[0.625rem] font-bold',
                  active ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-600',
                )}>{v.count}</span>
              )}
            </button>
            {v.isCustom && onSavedViewDelete && (
              <button
                type="button"
                onClick={() => { if (confirm(`Șterge șablonul „${v.label}"?`)) onSavedViewDelete(v.id); }}
                aria-label={`Șterge șablonul ${v.label}`}
                className="absolute -right-1 -top-1 inline-grid h-4 w-4 place-items-center rounded-full bg-neutral-200 text-[0.625rem] text-neutral-700 opacity-0 transition-opacity hover:bg-red-100 hover:text-red-700 group-hover:opacity-100"
              >×</button>
            )}
          </span>
        );
      })}
      {onSavedViewAdd && (
        <span className="ml-auto inline-flex">
          {adding ? (
            <form
              onSubmit={(e) => { e.preventDefault(); submitNew(); }}
              className="inline-flex items-center gap-1 px-2 py-1"
            >
              <input
                autoFocus
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onBlur={() => { if (!newLabel.trim()) setAdding(false); else submitNew(); }}
                onKeyDown={(e) => { if (e.key === 'Escape') setAdding(false); }}
                placeholder="Numele șablonului..."
                aria-label="Numele șablonului nou"
                className="rounded border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs text-neutral-900 outline-none focus:border-primary-500"
              />
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="rounded px-2 py-1 text-xs font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
            >+ Salvează șablon</button>
          )}
        </span>
      )}
    </nav>
  );
}
